/**
 * BCR任务配置 - 统一管理中英文映射
 * 包含9个BCR子任务的完整配置信息
 */

export interface BcrTaskConfig {
  name: string;           // 英文名称（后端标识）
  label: string;          // 中文标签
  description: string;    // 中文描述
  estimatedTime: string;  // 估计时间
  dependencies: string[]; // 依赖任务的英文名称
  order: number;          // 顺序索引（0-8）
}

export const BCR_TASK_CONFIGS: BcrTaskConfig[] = [
  {
    order: 0,
    name: 'change identification',
    label: '变更识别',
    description: '对比新旧版本文件变更',
    estimatedTime: '10-20分钟',
    dependencies: []
  },
  {
    order: 1,
    name: 'building bsw package',
    label: '构建BSW包',
    description: '上传产物、RMP注册、MCU/SOC构建',
    estimatedTime: '30-60分钟',
    dependencies: []
  },
  {
    order: 2,
    name: 'reserve bench',
    label: '预约台架',
    description: '通过Jenkins或BMP预约测试台架',
    estimatedTime: '2-5分钟',
    dependencies: ['building bsw package']
  },
  {
    order: 3,
    name: 'bench check after reserve',
    label: '预约后台架检查',
    description: '检查CAN通讯、版本、摄像头',
    estimatedTime: '5-10分钟',
    dependencies: ['reserve bench']
  },
  {
    order: 4,
    name: 'flash bsw package',
    label: '烧录BSW包',
    description: '将产物烧录到台架（可选）',
    estimatedTime: '5-15分钟',
    dependencies: ['bench check after reserve']
  },
  {
    order: 5,
    name: 'bench check',
    label: '台架检查',
    description: '检查CAN通讯、版本、摄像头',
    estimatedTime: '5-10分钟',
    dependencies: ['flash bsw package']
  },
  {
    order: 6,
    name: 'soc bsw testing',
    label: 'SOC测试',
    description: '执行SOC侧BSW功能测试',
    estimatedTime: '20-40分钟',
    dependencies: ['bench check']
  },
  {
    order: 7,
    name: 'mcu bsw testing',
    label: 'MCU测试',
    description: '执行MCU侧BSW功能测试',
    estimatedTime: '15-30分钟',
    dependencies: ['bench check']
  },
  {
    order: 8,
    name: 'release bench',
    label: '释放台架',
    description: '归还台架资源',
    estimatedTime: '2-5分钟',
    dependencies: ['reserve bench']
  }
];

/**
 * 创建快速查找Map：英文名 -> 配置对象
 * 用于O(1)时间复杂度的查询
 */
export const BCR_TASK_MAP = new Map<string, BcrTaskConfig>(
  BCR_TASK_CONFIGS.map(config => [config.name, config])
);

/**
 * 根据英文名称获取中文标签
 * @param name 英文任务名称
 * @returns 中文标签，未找到时返回原英文名称
 */
export const getBcrTaskLabel = (name: string): string => {
  return BCR_TASK_MAP.get(name)?.label || name;
};

/**
 * 获取完整的显示文本（中文 + 英文）
 * 格式：变更识别 (change identification)
 * @param name 英文任务名称
 * @returns "中文 (英文)" 格式的完整显示文本
 */
export const getBcrTaskDisplayName = (name: string): string => {
  const config = BCR_TASK_MAP.get(name);
  return config ? `${config.label} (${name})` : name;
};

/**
 * 根据任务名称获取报告链接标签
 * 格式：变更识别报告
 * @param taskName 英文任务名称
 * @returns 报告类型的中文标签
 */
export const getBcrReportLabel = (taskName: string): string => {
  const config = BCR_TASK_MAP.get(taskName);
  return config ? `${config.label}报告` : '测试报告';
};
